import { useMemo } from 'react';

// Border colors for categories (same hues as CATEGORY_COLORS but at 0.55 opacity)
const CATEGORY_BORDER_COLORS = [
  'rgba(59, 130, 246, 0.55)',   // blue
  'rgba(16, 185, 129, 0.55)',   // green
  'rgba(245, 158, 11, 0.55)',   // amber
  'rgba(239, 68, 68, 0.55)',    // red
  'rgba(139, 92, 246, 0.55)',   // purple
  'rgba(236, 72, 153, 0.55)',   // pink
  'rgba(6, 182, 212, 0.55)',    // cyan
  'rgba(234, 179, 8, 0.55)',    // yellow
  'rgba(249, 115, 22, 0.55)',   // orange
  'rgba(107, 114, 128, 0.55)'   // gray
];

// Faint background tints for inside groups (reinforces grouping in the gap)
const CATEGORY_BG_COLORS = [
  'rgba(59, 130, 246, 0.06)',
  'rgba(16, 185, 129, 0.06)',
  'rgba(245, 158, 11, 0.06)',
  'rgba(239, 68, 68, 0.06)',
  'rgba(139, 92, 246, 0.06)',
  'rgba(236, 72, 153, 0.06)',
  'rgba(6, 182, 212, 0.06)',
  'rgba(234, 179, 8, 0.06)',
  'rgba(249, 115, 22, 0.06)',
  'rgba(107, 114, 128, 0.06)'
];

const useCategoryGroupBorders = (slots, gridItems, rows, columns, enabled) => {
  return useMemo(() => {
    if (!enabled || !slots || !gridItems || gridItems.length === 0) {
      return { borders: new Map(), legend: [] };
    }

    // 1. Build occupancy grid: (row, col) -> category
    // Uses gridItems for accurate occupancy (handles double-width slots)
    const occupancy = new Map();

    gridItems.forEach(item => {
      if (item.kind !== 'slot' || !item.slot?.productCategory) return;
      const cat = item.slot.productCategory;
      for (let c = item.columnStart; c < item.columnStart + item.columnSpan; c++) {
        occupancy.set(`${item.row}-${c}`, cat);
      }
    });

    // 2. Assign colors to categories (stable ordering from first appearance)
    const seen = new Set();
    const categories = [];
    slots.forEach(s => {
      if (s?.productCategory && !seen.has(s.productCategory)) {
        seen.add(s.productCategory);
        categories.push(s.productCategory);
      }
    });

    const categoryColorMap = {};
    const categoryBgMap = {};
    categories.forEach((cat, idx) => {
      categoryColorMap[cat] = CATEGORY_BORDER_COLORS[idx % CATEGORY_BORDER_COLORS.length];
      categoryBgMap[cat] = CATEGORY_BG_COLORS[idx % CATEGORY_BG_COLORS.length];
    });

    // 3. For each gridItem, compute border directives
    const borders = new Map();

    gridItems.forEach(item => {
      if (item.kind !== 'slot' || !item.slot?.productCategory) return;

      const cat = item.slot.productCategory;
      const color = categoryColorMap[cat];
      const bgColor = categoryBgMap[cat];
      const { row, columnStart, columnSpan } = item;

      // Top: check all columns in span at row-1
      const hasTop = (() => {
        if (row <= 1) return true;
        for (let c = columnStart; c < columnStart + columnSpan; c++) {
          if (occupancy.get(`${row - 1}-${c}`) !== cat) return true;
        }
        return false;
      })();

      // Bottom: check all columns in span at row+1
      const hasBottom = (() => {
        if (row >= rows) return true;
        for (let c = columnStart; c < columnStart + columnSpan; c++) {
          if (occupancy.get(`${row + 1}-${c}`) !== cat) return true;
        }
        return false;
      })();

      // Left: check cell at (row, columnStart-1)
      const hasLeft = (() => {
        if (columnStart <= 1) return true;
        return occupancy.get(`${row}-${columnStart - 1}`) !== cat;
      })();

      // Right: check cell at (row, columnStart+columnSpan)
      const hasRight = (() => {
        if (columnStart + columnSpan > columns) return true;
        return occupancy.get(`${row}-${columnStart + columnSpan}`) !== cat;
      })();

      // Corner radius: round only outer corners
      const r = '6px';
      const topLeft = (hasTop && hasLeft) ? r : '0';
      const topRight = (hasTop && hasRight) ? r : '0';
      const bottomRight = (hasBottom && hasRight) ? r : '0';
      const bottomLeft = (hasBottom && hasLeft) ? r : '0';
      const borderRadius = `${topLeft} ${topRight} ${bottomRight} ${bottomLeft}`;

      // Border width: 2.5px for multi-cell groups, 2px for isolated cells
      const isIsolated = hasTop && hasBottom && hasLeft && hasRight;
      const borderWidth = isIsolated ? '2px' : '2.5px';

      borders.set(`${row}-${columnStart}`, {
        top: hasTop,
        right: hasRight,
        bottom: hasBottom,
        left: hasLeft,
        color,
        bgColor,
        borderRadius,
        borderWidth
      });
    });

    // 4. Build legend
    const legend = categories.map((name, idx) => ({
      name,
      color: CATEGORY_BORDER_COLORS[idx % CATEGORY_BORDER_COLORS.length]
    }));

    return { borders, legend };
  }, [slots, gridItems, rows, columns, enabled]);
};

export default useCategoryGroupBorders;
